import { useEffect, useLayoutEffect, useState, useSyncExternalStore, type RefObject } from "react";

/**
 * The arrival (Figma 11 archive · M01 desktop, M02 phone, M05 counter, M06
 * month strip, M10 reduced). One sweep in reading order when Home opens, when
 * the brand changes, or when the tab comes back after 30 minutes away. Never on
 * a poll.
 *
 * The sweep itself is CSS, keyed on `.home[data-arrive]` (home.css). This
 * module sets that attribute, clears it once the sweep is done (so a row a poll
 * adds later does not animate in), and tells the counters a new arrival began.
 */

const AWAY_MS = 30 * 60_000;
/** Longest step ends at 1.2s (desktop); the attribute goes a beat after. */
const SWEEP_MS = 1400;

let seq = 0;
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSeq = () => seq;

/** Which arrival the page is on; a counter counts once per arrival. */
export function useArrivalSeq(): number {
  return useSyncExternalStore(subscribe, getSeq, getSeq);
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return (
    document.documentElement.dataset.coMotion === "reduce" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export const isPhoneHome = () => window.matchMedia("(max-width: 767px)").matches;

function play(home: HTMLElement | null) {
  if (!home) return;
  home.removeAttribute("data-arrive");
  home.removeAttribute("data-arrive-scroll");
  home.querySelectorAll("[data-seen]").forEach((el) => el.removeAttribute("data-seen"));
  // Restart every CSS animation keyed on the attribute.
  void home.offsetWidth;
  home.setAttribute("data-arrive", "");
  // M02: on a phone the sections under the band arrive as they scroll in.
  if (isPhoneHome()) home.setAttribute("data-arrive-scroll", "");
  seq += 1;
  listeners.forEach((fn) => fn());
  window.setTimeout(() => {
    home.removeAttribute("data-arrive");
  }, SWEEP_MS);
}

/**
 * Plays the arrival for a brand the page has not arrived on yet (the page only
 * mounts once its figures are in), and again when the tab returns after 30
 * minutes away.
 */
export function useArrival(home: RefObject<HTMLElement | null>, brandKey: string) {
  const [arrivedFor, setArrivedFor] = useState<string | null>(null);
  if (arrivedFor !== brandKey) setArrivedFor(brandKey);

  // Layout effect: the counters drop to their dim zero before the first paint.
  useLayoutEffect(() => {
    if (arrivedFor) play(home.current);
  }, [arrivedFor, home]);

  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt >= AWAY_MS) play(home.current);
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [home]);

  // M02: mark each section as it scrolls in (phone arrivals only).
  useEffect(() => {
    const root = home.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && root.hasAttribute("data-arrive-scroll")) {
            entry.target.setAttribute("data-seen", "");
          }
        }
      },
      { threshold: 0.15 },
    );
    root.querySelectorAll(".home-sec").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  });
}

/**
 * M05 · a counter. Counts up from zero to its value in its turn (60ms apart on
 * desktop, 40 on a phone; 300ms / 250ms snappy). A zero does not count. A value
 * that changes later swaps in (the page fades it). Reduced motion: no count.
 */
export function useCountUp(value: number | null, index: number): { shown: number | null; counting: boolean } {
  const arrival = useArrivalSeq();
  const [played, setPlayed] = useState(0);
  const [frame, setFrame] = useState<number | null>(null);
  const pending = arrival !== played && value !== null && value > 0 && !prefersReducedMotion();

  useEffect(() => {
    if (arrival === played) return;
    if (!pending || value === null) {
      const id = requestAnimationFrame(() => setPlayed(arrival));
      return () => cancelAnimationFrame(id);
    }
    const phone = isPhoneHome();
    const delay = phone ? 200 + index * 40 : 150 + index * 60;
    const duration = phone ? 250 : 300;
    let raf = 0;
    const timer = window.setTimeout(() => {
      const start = performance.now();
      const step = (now: number) => {
        // A frame stamp can sit just before `start`; never count below zero.
        const t = Math.min(1, Math.max(0, (now - start) / duration));
        // snappy: a fast critically damped settle.
        const eased = 1 - Math.pow(1 - t, 3);
        setFrame(Math.round(eased * value));
        if (t < 1) raf = requestAnimationFrame(step);
        else {
          setFrame(null);
          setPlayed(arrival);
        }
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [arrival, played, pending, value, index]);

  return { shown: pending ? (frame ?? 0) : value, counting: pending };
}
